import { useContext, useEffect, useMemo, useReducer, useState } from "react";
import { useParams } from "react-router";

import { ApiHelper } from "@datanac/datanac-api-toolkit/dist/api-helper";
import {
  searchInsuranceOfferPractice,
  searchInsuranceOfferType,
} from "@datanac/datanac-api-toolkit/dist/utility/InsuranceOfferHelper";
import { getCurrentReinsuranceYear } from "@datanac/datanac-api-toolkit/dist/utility/InsurancePlanHelper";
import { useCardController, useDialog } from "@datanac/datanac-mui-toolkit";
import { round_ex } from "@datanac/datanac-visualization-toolkit/dist/utility/Math";
import { FormatListBulleted } from "@mui/icons-material";
import AddCircleOutline from "@mui/icons-material/AddCircleOutline";
import ArrowDropDownOutlined from "@mui/icons-material/ArrowDropDownOutlined";
import CloseOutlined from "@mui/icons-material/CloseOutlined";
import EditOutlined from "@mui/icons-material/EditOutlined";
import RestartAlt from "@mui/icons-material/RestartAlt";
import InfoOutlined from "@mui/icons-material/InfoOutlined";
import {
  Card,
  CardContent,
  Dialog,
  DialogActions,
  DialogContent,
  FormControl,
  Grid,
  InputAdornment,
  Menu,
  MenuItem,
  Stack,
  useMediaQuery,
} from "@mui/material";
import Box from "@mui/material/Box";
import Button from "@mui/material/Button";
import Divider from "@mui/material/Divider";
import IconButton from "@mui/material/IconButton";
import Typography from "@mui/material/Typography";
import { AppContext } from "App";
import { getUnitOfMeasureAbbreviation } from "components/Budget/BudgetService";
import DataFormSelect from "components/Core/DataFormSelect";
import { propUpdateReducer } from "components/Core/propUpdateReducer";
import { MASK_NUMERIC, StatefulControlledTextField } from "components/Core/StatefulControlledTextField";
import CountyDataView from "components/County/CountyDataView";
import { BudgetIcon } from "components/Icons/Icons";
import { useBlockerPrompt } from "components/Menu/hooks/useBlockerPrompt";
import usePrompt from "components/Utility/usePrompt";
import WorkspaceHeader from "components/Workspaces/WorkspaceHeader";
import _ from "lodash";
import PopupState, { bindMenu, bindTrigger } from "material-ui-popup-state";
import { ChatAssistantButton } from "mui-toolkit/ChatAssistantButton";
import { useFormStatus } from "mui-toolkit/useFormStatus";
import { useSnackbar } from "notistack";
import { formatBasis, formatNumber, formatPrice } from "utility/formatters";
import { v4 as uuidv4 } from "uuid";

import {
  _sessionManager,
  AipPassApiHelper,
  ApiHelper as ApiHelperWrapper,
  InsurancePlanHelper,
  RmaApiHelper,
  UsersApiHelper,
} from "../../api/ApiHelper";
import { useChatAssistantAttachments } from "../../contexts/useChatAssistantAttachments";
import { MASK_PERCENTAGE } from "../Core/StatefulControlledTextField";
import { DocumentSelectDialog } from "../Financial/DocumentSelectDialog";
import { DOCUMENT_FORM_NAME_BUDGET_CROPS } from "../Financial/FinancialHelpers";
import { DeleteIcon, FarmVaultIcon } from "../Icons/Icons";
import { useWeatherReport } from "../Weather/useWeatherReport";
import { YieldForecast } from "../Weather/YieldForecast";
import { CropProfileEditDialog } from "../Broker/CropProfileEditDialog";
import { BudgetSchedulePopover } from "./BudgetSchedulePopover";
import BudgetTemplateDialog from "./components/BudgetTemplateDialog";
import {
  BUDGET_CATEGORY_DIRECT,
  BUDGET_CATEGORY_INDIRECT,
  BUDGET_ITEMS_DIRECT,
  BUDGET_ITEMS_INDIRECT,
} from "./helpers";
import { deriveProjectedPrice, shouldPersistDerivedProjectedPrice } from "./projectedPrice";

export const SYSTEM_PROMPT_BUDGET_COUNTY_PRACTICE = "form_budgetCountyPractice";

export default function BudgetCountyPracticeWorkspace() {
  const { globalState } = useContext(AppContext);
  const { budget_id, budget_county_practice_id } = useParams();

  const isXL = useMediaQuery((theme) => theme.breakpoints.up("xl"), { noSsr: true });
  const isSmall = useMediaQuery((theme) => theme.breakpoints.down("md"), { noSsr: true });
  const { enqueueSnackbar } = useSnackbar();
  const [isLoading, setIsLoading] = useState(true);
  const { component: formStatusComponent, actions: formStatusActions, state: formStatusState } = useFormStatus();
  const { addAttachments } = useChatAssistantAttachments();

  const [state, dispatch] = useReducer(propUpdateReducer, {
    is_active: true,
  });
  const { state: lineItems, actions: lineItemActions } = useCardController();
  const { component: confirmDialogComponent } = useDialog();
  const prompt = usePrompt();

  const [futures, setFutures] = useState({});

  const [anchorEl, setAnchorEl] = useState(null);
  const [selectedField, setSelectedField] = useState(null);

  const [unitOfMeasureAbbreviation, setUnitOfMeasureAbbreviation] = useState("");

  useEffect(() => {
    if (isValid()) {
      _getUnitOfMeasureAbbreviation(state);
    }
  }, [state]);

  const _getUnitOfMeasureAbbreviation = async (countyPractice) => {
    const _unitOfMeasureAbbreviation = (await getUnitOfMeasureAbbreviation(countyPractice)) || "";
    setUnitOfMeasureAbbreviation(_unitOfMeasureAbbreviation);
  };

  const handleFieldClick = (event, currentItem) => {
    setSelectedField(currentItem);
    setAnchorEl(event.currentTarget);
  };

  const handlePopoverClose = async (_budgetSchedule) => {
    setBudgetSchedule(_budgetSchedule);

    if (selectedField) {
      const totalSubtotal = _budgetSchedule
        .filter(
          (row) =>
            row.line_item_name === selectedField.name &&
            row.category === selectedField.category &&
            row.is_active !== false
        )
        .reduce((sum, row) => sum + Number(row.quantity || 0) * Number(row.unit_value || 0), 0);

      handleChangeState_LineItem({ target: { value: round_ex(totalSubtotal, 2) } }, selectedField);

      const hasActiveScheduleItems = _budgetSchedule.some(
        (row) =>
          row.line_item_name === selectedField.name &&
          row.category === selectedField.category &&
          row.is_active !== false
      );

      const updatedLineItem = {
        ...selectedField,
        value: round_ex(totalSubtotal, 2),
        isDisabled: hasActiveScheduleItems,
      };

      lineItemActions.update(updatedLineItem);
    }

    setAnchorEl(null);
    setSelectedField(null);
  };

  const isPopoverOpen = Boolean(anchorEl);
  const handleError = (err) => {
    console.warn(err);
    enqueueSnackbar(err.detail || err.message || JSON.stringify(err), { preventDuplicate: true, variant: "error" });
  };

  const loadBudgetScheduleData = async (currentItem = null) => {
    try {
      const api = new ApiHelper({
        baseUrl: import.meta.env.VITE_DATANAC_USERS_API_URL_BASE,
        config: {},
        sessionManager: _sessionManager,
        _instance: null, // No caching
      });

      const _filter = {
        producer_token: globalState?.producer_token,
        is_active: true,
        budget_county_practice_id: state?.id,
        ...(currentItem?.name && { line_item_name: currentItem.name }), // Only include if defined
      };

      const scheduleItems = await api.callPost("BudgetCountyPracticeScheduleItem/Search", _filter, globalState);

      setBudgetSchedule(scheduleItems);
      return scheduleItems;
    } catch (err) {
      handleError(err);
    }
  };

  const loadFuturesData = async () => {
    try {
      if (
        state.year &&
        state.state_name &&
        state.county_name &&
        state.commodity_name &&
        state.type_name &&
        state.practice_name
      ) {
        const futuresParameters = {
          reinsurance_year: state.year,
          state_name: state.state_name,
          county_name: state.county_name,
          commodity_name: state.commodity_name,
          type_name: state.type_name,
          practice_name: state.practice_name,
        };
        ApiHelperWrapper.callSmociObject(
          "data/DATANAC",
          "Markets",
          "Futures",
          "Search",
          futuresParameters,
          globalState
        ).then((_futures) => {
          if (_futures?.length) {
            setFutures(_futures[0]);
          } else {
            setFutures(null);
          }
        });
      }
    } catch (err) {
      handleError(err);
    }
  };

  const [prices, setPrices] = useState({});
  const [boardPrice, setBoardPrice] = useState(null);
  const [boardPriceMarketSymbol, setBoardPriceMarketSymbol] = useState(null);
  const [cropBasis, setCropBasis] = useState(null);
  const [cropProfile, setCropProfile] = useState(null);
  const [isBasisDialogOpen, setIsBasisDialogOpen] = useState(false);

  const loadPriceData = async () => {
    try {
      if (state.state_name && state.county_name && state.commodity_name && state.type_name && state.practice_name) {
        const pricesParameters = {
          reinsurance_year: String(state.year),
          state_name: state.state_name,
          county_name: state.county_name,
          commodity_name: state.commodity_name,
          type_name: state.type_name,
          practice_name: state.practice_name,
        };

        RmaApiHelper.selectPrices(pricesParameters).then((_prices) => {
          if (_prices?.length) {
            setPrices(_prices[0]);
          }
        });
      }
    } catch (err) {
      handleError(err);
    }
  };

  const loadData = async () => {
    try {
      setIsLoading(true);

      const budget = await UsersApiHelper.selectBudgets({ id: budget_id, is_active: true }, globalState);
      if (budget?.length) {
        dispatch({
          type: "updateMany",
          payload: {
            budget_id: budget[0]?.id,
            budget_name: budget[0]?.name,
            year: budget[0]?.year,
          },
        });
      }

      if (state?.id && String(state?.id) !== "0") {
        const budgetCountyPractice = await UsersApiHelper.selectBudgetCountyPractices(
          {
            budget_id: budget_id,
            id: state?.id,
            is_active: true,
          },
          globalState
        );
        if (budgetCountyPractice?.length) {
          dispatch({
            type: "updateMany",
            payload: budgetCountyPractice[0],
          });
        }
      }

      if (state?.id && String(state?.id) !== "0") {
        const budgetCountyPracticeLineItems = await UsersApiHelper.selectBudgetCountyPracticeLineItems(
          {
            budget_county_practice_id: state?.id,
            is_active: true,
          },
          globalState
        );
        if (budgetCountyPracticeLineItems?.length) {
          const budgetLineItems = await loadBudgetScheduleData(null);

          const scheduledItemsSet = new Set(
            budgetLineItems
              .filter((budgetItem) => budgetItem.is_active !== false)
              .map((budgetItem) => `${budgetItem.category}::${budgetItem.line_item_name}`)
          );
          const updatedLineItems = budgetCountyPracticeLineItems.map((item) => ({
            ...item,
            isDisabled: scheduledItemsSet.has(`${item.category}::${item.name}`),
          }));
          lineItemActions.replace(updatedLineItems);
          setBudgetSchedule(budgetLineItems);
        } else {
          // Load default:
          const _defaultLineItems = createDefaultLineItems();
          lineItemActions.replace(_defaultLineItems);
        }
      }
      setIsLoading(false);
    } catch (err) {
      setIsLoading(false);
      dispatch({
        type: "replace",
        payload: {},
      });
      lineItemActions.replace([]);
      handleError(err);
    }
  };

  const createDefaultLineItems = () => {
    var _defaultLineItems = [];
    _defaultLineItems = _defaultLineItems.concat(
      BUDGET_ITEMS_DIRECT.map((ii) => ({
        ...createLineItem(BUDGET_CATEGORY_DIRECT, ii[1], 0.0, false /*doOverwrite*/),
        isDisabled: false,
      }))
    );

    _defaultLineItems = _defaultLineItems.concat(
      BUDGET_ITEMS_INDIRECT.map((ii) => ({
        ...createLineItem(BUDGET_CATEGORY_INDIRECT, ii[1], 0.0, false /*doOverwrite*/),
        isDisabled: false,
      }))
    );

    return _defaultLineItems;
  };

  useEffect(() => {
    if (
      globalState?.producer_token &&
      state?.budget_id !== null &&
      state?.budget_id !== undefined &&
      state?.id !== null &&
      state?.id !== undefined
    ) {
      loadData();
    }
  }, [state?.budget_id, state?.id]);

  useEffect(() => {
    dispatch({
      type: "update",
      payload: { key: "budget_id", value: budget_id },
    });

    if (String(budget_county_practice_id) === "0" && globalState?.producer_token) {
      const _budget_county_practice_id = uuidv4();
      dispatch({
        type: "update",
        payload: { key: "id", value: _budget_county_practice_id },
      });
    } else {
      dispatch({
        type: "update",
        payload: { key: "id", value: budget_county_practice_id },
      });
    }
  }, [globalState?.producer_token, budget_id, budget_county_practice_id]);

  useEffect(() => {
    loadFuturesData();
    loadPriceData();
  }, [state?.state_name, state?.county_name, state?.commodity_name, state?.type_name, state?.practice_name]);

  useEffect(() => {
    const fetchPricingData = async () => {
      if (!state?.year || !state?.commodity_name || !globalState?.producer_token) {
        setBoardPrice(null);
        setBoardPriceMarketSymbol(null);
        setCropBasis(null);
        return;
      }

      try {
        const [positionSummary, cropProfiles] = await Promise.all([
          UsersApiHelper.users_search(
            "PositionSummary",
            { year: state.year, producer_token: globalState.producer_token, commodity_name: state.commodity_name },
            globalState
          ),
          UsersApiHelper.users_search(
            "CropProfile",
            {
              producer_token: globalState.producer_token,
              year: state.year,
              commodity_name: state.commodity_name,
              is_active: true,
            },
            globalState
          ),
        ]);

        const row = positionSummary?.[0];
        setBoardPrice(row?.market_last_price ?? null);
        setBoardPriceMarketSymbol(row?.market_symbol ?? null);
        setCropBasis(cropProfiles?.[0]?.basis ?? null);
        setCropProfile(cropProfiles?.[0] ?? null);
      } catch (err) {
        handleError(err);
      }
    };

    fetchPricingData();
  }, [state?.year, state?.commodity_name, globalState?.producer_token]);

  useEffect(() => {
    if (!state?.projected_price_is_override && boardPrice != null) {
      const derived = deriveProjectedPrice(boardPrice, cropBasis);
      // Cards and reports read stored projected_price. Persist whenever derived
      // board+basis differs — not only when stored is 0 (Gaum corn $5.22 vs $5.25,
      // soy $10 vs $12.715 left cards stale).
      if (shouldPersistDerivedProjectedPrice(state?.projected_price, derived)) {
        formStatusActions.setIsFormDirty(true, false);
      }
      dispatch({
        type: "updateMany",
        payload: {
          projected_price: derived,
          projected_price_is_override: false,
        },
      });
    }
  }, [boardPrice, cropBasis, state?.projected_price_is_override]);

  useEffect(() => {
    if (
      String(budget_county_practice_id) === "0" &&
      String(budget_id) !== "0" &&
      state?.id &&
      lineItems.filter((li) => li.is_active)?.length === 0
    ) {
      // Load default:
      const _defaultLineItems = createDefaultLineItems();
      lineItemActions.replace(_defaultLineItems);
    }
  }, [state?.id, budget_county_practice_id, budget_id, lineItems]);

  useEffect(() => {
    if (isValid() && globalState.producer_token && formStatusState?.isFormDirty) {
      formStatusActions.setIsFormDirty(true, true);
      // Header only. Rewriting line items here fired a second insert of the
      // same new ids during document import (PK_BudgetCountyPracticeLineItem).
      const requestBody = {
        BudgetCountyPractice: [state],
        budget_id: budget_id,
        producer_token: globalState.producer_token,
      };
      UsersApiHelper.updateBudgetCountyPractice(requestBody, globalState)
        .then(() => {
          formStatusActions.setIsSaving(false);
        })
        .catch((err) => {
          console.warn(err);
          formStatusActions.setIsFormDirty(true, false);
          enqueueSnackbar(err.detail || err.message || JSON.stringify(err), {
            preventDuplicate: true,
            variant: "error",
          });
        });
    }
  }, [state]);

  const isValid = () => {
    return (
      state?.id &&
      state?.state_name &&
      state?.county_name &&
      state?.commodity_name &&
      state?.type_name &&
      state?.practice_name &&
      state?.reported_acreage &&
      state?.yield &&
      state?.projected_price
    );
  };
  useBlockerPrompt(!isValid(), "BudgetCountyPracticeWorkspace");

  useEffect(() => {
    if (globalState?.producer_token && lineItems && formStatusState?.isFormDirty) {
      formStatusActions.setIsFormDirty(true, true);

      UsersApiHelper.updateBudgetCountyPracticeLineItem({ BudgetCountyPracticeLineItems: lineItems }, globalState)
        .then(() => {
          formStatusActions.setIsSaving(false);
        })
        .catch((err) => {
          console.warn(err);
          formStatusActions.setIsFormDirty(true, false);
          enqueueSnackbar(err.detail || err.message || JSON.stringify(err), {
            preventDuplicate: true,
            variant: "error",
          });
        });
    }
  }, [lineItems]);

  const calculateProductionCost = () => {
    return _.sumBy(
      lineItems.filter((ii) => ii.is_active),
      (i) => Number(i.value) || 0
    );
  };

  const handleChangeState = async (event, value) => {
    if (!event?.target) return;

    const eventValue = event.target.value;
    const eventName = event.target.name;

    value = value ?? eventValue;

    // A blur of the value just committed (Enter selects the field, then the
    // user leaves it) must not mark dirty. Nothing is dispatched, so the save
    // effect never runs, and "Changes are not saved..." stays up.
    if (state[eventName] === value) return;

    formStatusActions.setIsFormDirty(true, false);

    if (eventName === "production_share_percent") {
      value = value === "" ? null : Number(value);
    }

    if (eventName === "projected_price") {
      dispatch({
        type: "update",
        payload: { key: "projected_price_is_override", value: true },
      });
    }

    if (eventName === "state_name") {
      dispatch({
        type: "updateMany",
        payload: {
          state_name: value,
          county_name: "",
          commodity_name: "",
          type_name: "",
          practice_name: "",
        },
      });
    } else if (eventName === "county_name") {
      dispatch({
        type: "updateMany",
        payload: {
          county_name: value,
          commodity_name: "",
          type_name: "",
          practice_name: "",
        },
      });
    } else if (eventName === "commodity_name") {
      dispatch({
        type: "updateMany",
        payload: {
          commodity_name: value,
          type_name: "",
          practice_name: "",
        },
      });
    } else if (eventName === "type_name") {
      dispatch({
        type: "updateMany",
        payload: {
          type_name: value,
          practice_name: "",
        },
      });
    } else {
      dispatch({
        type: "update",
        payload: { key: eventName, value: value },
      });
    }
  };
  const handleBasisSave = async (_newCropProfile) => {
    try {
      const api = new ApiHelper({
        baseUrl: import.meta.env.VITE_DATANAC_USERS_API_URL_BASE,
        config: {},
        sessionManager: _sessionManager,
        _instance: null,
      });

      const payload = {
        id: cropProfile?.id || uuidv4(),
        producer_token: globalState.producer_token,
        is_active: true,
        ..._newCropProfile,
        year: state?.year,
        commodity_name: state?.commodity_name,
        name: _newCropProfile?.name || state?.commodity_name,
      };

      await api.callPost("marketing/CropProfile", payload, { agent_token: globalState?.agent_token });

      // Do not reload the row here. That write-back puts the stored
      // projected_price over board + basis, and 0 shows as a blank field.
      // setCropBasis lets the derive effect update the price, and the dirty
      // flag persists it.
      setCropBasis(payload.basis ?? null);
      setCropProfile(payload);
      formStatusActions.setIsFormDirty(true, false);
    } catch (err) {
      handleError(err);
    }
  };

  const handleChangeState_LineItem = async (event, currentLineItem) => {
    formStatusActions.setIsFormDirty(true, false);
    const _newValue = {
      ...currentLineItem,
      value: event?.target?.value || 0,
    };

    lineItemActions.update(_newValue);
  };

  const handleClickAddNewLineItem = async (category) => {
    if (state?.id) {
      prompt.actions.prompt({
        title: "Budget Item",
        prompt: "Please enter budget item name:",
        callback: (inputValue) => {
          if (inputValue) {
            const newLineItem = {
              ...createLineItem(category, inputValue, 0.0, false /*doOverwrite*/),
              isDisabled: false,
            };
            lineItemActions.add(newLineItem);
          }
        },
      });
    }
  };

  const [isDeletingLineItem, setIsDeletingLineItem] = useState(false);

  const handleDeleteLineItem = async (currentLineItem) => {
    // Prevent multiple simultaneous delete operations
    if (isDeletingLineItem) return;

    try {
      setIsDeletingLineItem(true);

      const updateSchedule = budgetSchedule
        .filter((row) => row.line_item_name === currentLineItem?.name && row.category === currentLineItem?.category)
        .map((row) => {
          row.is_active = false;
          return row;
        });
      await updateBudgetCountySchedule(updateSchedule);

      const _newLineItem = { ...currentLineItem };
      _newLineItem.is_active = false;
      lineItemActions.update(_newLineItem);
      formStatusActions.setIsFormDirty(true, true);

      // Load data after all updates are complete
      await loadData();
    } catch (error) {
      console.error("Error deleting line item:", error);
      // Handle error appropriately (show user notification, etc.)
    } finally {
      setIsDeletingLineItem(false);
    }
  };

  const commodityDataFormSelect = useMemo(() => {
    return (
      <DataFormSelect
        value={state.commodity_name || ""}
        name="commodity_name"
        onChange={handleChangeState}
        objectNameLabel="Commodity"
        itemGetKey={(item) => item.commodity_name}
        itemGetLabel={(item) => item.commodity_name}
        doFetch={state?.state_name && state?.county_name}
        isLoading={isLoading}
        customDataLoader={() =>
          RmaApiHelper.searchInsuranceOfferCommodity(
            {
              state_name: state?.state_name,
              county_name: state?.county_name,
            },
            globalState
          )
        }
      />
    );
  }, [state?.state_name, state?.county_name, state.commodity_name, isLoading]);

  const typeDataFormSelect = useMemo(() => {
    return (
      <DataFormSelect
        value={state.type_name || ""}
        name="type_name"
        onChange={handleChangeState}
        objectNameLabel="Type"
        itemGetKey={(item) => item.type_name}
        itemGetLabel={(item) => item.type_name}
        doFetch={state?.state_name && state?.county_name && state.commodity_name}
        isLoading={isLoading}
        customDataLoader={() =>
          searchInsuranceOfferType(
            {
              state_name: state?.state_name,
              county_name: state?.county_name,
              commodity_name: state.commodity_name,
            },
            RmaApiHelper,
            state?.year
          )
        }
      />
    );
  }, [state?.state_name, state?.county_name, state.commodity_name, state.type_name, isLoading]);

  const practiceDataFormSelect = useMemo(() => {
    return (
      <DataFormSelect
        value={state.practice_name || ""}
        name="practice_name"
        onChange={handleChangeState}
        objectNameLabel="Practice"
        itemGetKey={(item) => item.practice_name}
        itemGetLabel={(item) => item.practice_name}
        doFetch={state?.state_name && state?.county_name && state.commodity_name && state.type_name}
        isLoading={isLoading}
        customDataLoader={() =>
          searchInsuranceOfferPractice(
            {
              state_name: state?.state_name,
              county_name: state?.county_name,
              commodity_name: state.commodity_name,
              type_name: state.type_name,
            },
            RmaApiHelper,
            state?.year
          )
        }
      />
    );
  }, [state?.state_name, state?.county_name, state.commodity_name, state.type_name, state.practice_name, isLoading]);

  const [isTemplateDialogOpen, setIsTemplateDialogOpen] = useState(false);
  const [isDocumentSelectDialogOpen, setIsDocumentSelectDialogOpen] = useState(false);

  const getBudgetCategory = (lineItemName) => {
    if (BUDGET_ITEMS_DIRECT.some(([itemKey]) => itemKey === lineItemName)) {
      return "direct";
    } else if (BUDGET_ITEMS_INDIRECT.some(([itemKey]) => itemKey === lineItemName)) {
      return "indirect";
    }
  };

  const loadBudgetScheduleTemplateData = async (selectedTemplate, importedLineItems) => {
    const budgetScheduleParameters = {
      year: selectedTemplate.year,
      state_name: selectedTemplate.state_name,
      county_name: selectedTemplate.county_name,
      commodity_name: selectedTemplate.commodity_name,
      budget_name: selectedTemplate.name,
    };

    ApiHelperWrapper.callSmociObject(
      "data/DATANAC",
      "FSA.Extension",
      "BudgetSchedule",
      "Search",
      budgetScheduleParameters,
      globalState
    )
      .then(async (budgetSchedules) => {
        if (budgetSchedules && budgetSchedules?.length > 0) {
          const _mappedBudgetScheduleTemplateData = budgetSchedules.map((_bst) => {
            // Destructure to exclude fields that should not be copied from template
            const { state_name, county_name, commodity_name, type_name, practice_name, ..._bstWithoutLocationFields } =
              _bst;

            return {
              ..._bstWithoutLocationFields,
              id: uuidv4(),
              producer_token: globalState.producer_token,
              budget_county_practice_id: state?.id,
              budget_id: budget_id,
              year: state.year,
              quantity: _bst.quantity || 1,
              unit_of_measure: _bst.unit_of_measure || "each",
              unit_value: _bst.total,
              value: _bst.total,
              ordinal: _bst.ordinal || 0,
              case_quantity: 0,
              case_unit_of_measure: "each",
              is_active: true,
              category: _bst.category || getBudgetCategory(_bst.tilley_line_item_name),
              input_share_percent: _bst.input_share_percent || null,
            };
          });

          const scheduleItemsSet = new Set(
            _mappedBudgetScheduleTemplateData.map((schedule) => `${schedule.category}::${schedule.line_item_name}`)
          );
          const lineItemsWithSchedules = importedLineItems.map((item) => ({
            ...item,
            isDisabled: scheduleItemsSet.has(`${item.category}::${item.name}`),
          }));

          lineItemActions.replace(lineItemsWithSchedules);
          await updateBudgetCountySchedule(_mappedBudgetScheduleTemplateData);
        }
      })
      .catch((err) => {
        handleError(err);
      });
  };

  const loadSelectedTemplate = async (selectedTemplate) => {
    try {
      formStatusActions.setIsFormDirty(true, true);

      const newValuesDirect = {};
      BUDGET_ITEMS_DIRECT.forEach((i) => {
        newValuesDirect[i[1]] = selectedTemplate[i[0]];
      });

      const templateDirectLineItems = Object.keys(newValuesDirect).map((key) =>
        createLineItem(BUDGET_CATEGORY_DIRECT, key, newValuesDirect[key], true /*doOverwrite*/)
      );

      const newValuesIndirect = {};
      BUDGET_ITEMS_INDIRECT.forEach((i) => {
        newValuesIndirect[i[1]] = selectedTemplate[i[0]];
      });
      const templateIndirectLineItems = Object.keys(newValuesIndirect).map((key) =>
        createLineItem(BUDGET_CATEGORY_INDIRECT, key, newValuesIndirect[key], true /*doOverwrite*/)
      );

      const allTemplateLineItems = [...templateDirectLineItems, ...templateIndirectLineItems];
      lineItemActions.replace(allTemplateLineItems);

      if (budgetSchedule.length > 0) {
        await removeBudgetScheduleData();
      }

      await loadBudgetScheduleTemplateData(selectedTemplate, allTemplateLineItems);
    } catch (error) {
      console.error("Error applying template:", error);
      enqueueSnackbar("Error applying template: " + (error.message || error), {
        preventDuplicate: true,
        variant: "error",
      });
    } finally {
      formStatusActions.setIsFormDirty(false, false);
    }
  };

  const removeBudgetScheduleData = async () => {
    const _budgetScheduleData = budgetSchedule.map((_bst) => {
      return {
        ..._bst,
        is_active: false,
      };
    });

    await updateBudgetCountySchedule(_budgetScheduleData);
  };

  const createLineItem = (category, name, value, doOverwrite) => {
    const _id = doOverwrite && lineItems.find((li) => li?.name === name)?.id;

    const _newLineItem = {
      id: _id || uuidv4(),
      producer_token: globalState.producer_token,
      budget_county_practice_id: state?.id,
      category: category,
      name: name,
      value: value,
      is_active: true,
    };

    return _newLineItem;
  };

  const [isCountyDataDialogOpen, setIsCountyDataDialogOpen] = useState(false);

  const handleTemplateDialogOpen = () => {
    setIsTemplateDialogOpen(true);
  };

  const handleTemplateDialogClose = () => {
    setIsTemplateDialogOpen(false);
  };

  const handleTemplateSelect = async (selectedTemplate) => {
    await loadSelectedTemplate(selectedTemplate);
    setIsTemplateDialogOpen(false);
    enqueueSnackbar("Template applied successfully!", {
      preventDuplicate: true,
      variant: "success",
    });
  };

  const DocumentImport_Update = async (documentContents) => {
    try {
      formStatusActions.setIsFormDirty(true, true);

      // Parse the JSON and add required fields to top-level object
      const parsedData = typeof documentContents === "string" ? JSON.parse(documentContents) : documentContents;

      // Update the top-level budget county practice state
      const topLevelFields = {
        state_name: parsedData.state_name,
        county_name: parsedData.county_name,
        commodity_name: parsedData.commodity_name,
        type_name: parsedData.type_name,
        practice_name: parsedData.practice_name,
        name: parsedData.name,
        yield: parsedData.yield,
        projected_price: parsedData.projected_price,
      };

      // Update state with top-level fields
      dispatch({
        type: "updateMany",
        payload: topLevelFields,
      });

      // Process BudgetCountyPracticeLineItems
      if (parsedData.BudgetCountyPracticeLineItems && parsedData.BudgetCountyPracticeLineItems.length > 0) {
        const processedLineItems = parsedData.BudgetCountyPracticeLineItems.map((lineItem) => {
          // Create base line item with required fields
          const processedLineItem = {
            id: uuidv4(), // Generate new unique ID
            producer_token: globalState.producer_token,
            budget_county_practice_id: state?.id, // Add nested ID
            budget_id: budget_id, // Add budget_id
            category: lineItem.category,
            name: lineItem.name,
            value: 0, // Will be calculated from schedule items
            is_active: true,
          };

          return processedLineItem;
        });

        // Process BudgetCountyPracticeScheduleItems
        const allScheduleItems = [];

        parsedData.BudgetCountyPracticeLineItems.forEach((lineItem) => {
          if (lineItem.BudgetCountyPracticeScheduleItems && lineItem.BudgetCountyPracticeScheduleItems.length > 0) {
            lineItem.BudgetCountyPracticeScheduleItems.forEach((scheduleItem) => {
              const processedScheduleItem = {
                id: uuidv4(), // Generate new unique ID
                producer_token: globalState.producer_token,
                budget_county_practice_id: state?.id, // Add nested ID
                budget_id: budget_id, // Add budget_id
                year: state.year,
                category: lineItem.category,
                line_item_name: lineItem.name,
                name: scheduleItem.name,
                quantity: scheduleItem.quantity || 1,
                unit_of_measure: scheduleItem.unit_of_measure || "each",
                case_quantity: scheduleItem.case_quantity || 1,
                case_unit_of_measure: scheduleItem.case_unit_of_measure || "",
                unit_value: scheduleItem.unit_value || 0,
                value: scheduleItem.value || 0,
                is_active: true,
                ordinal: 0,
                input_share_percent: null,
              };

              allScheduleItems.push(processedScheduleItem);
            });
          }
        });

        // Calculate line item values from schedule items
        const lineItemTotals = {};
        allScheduleItems.forEach((scheduleItem) => {
          const key = `${scheduleItem.category}::${scheduleItem.line_item_name}`;
          const total = Number(scheduleItem.quantity || 0) * Number(scheduleItem.unit_value || 0);
          lineItemTotals[key] = round_ex((lineItemTotals[key] || 0) + total, 2);
        });

        // Update line item values with calculated totals
        const updatedLineItems = processedLineItems.map((lineItem) => {
          const key = `${lineItem.category}::${lineItem.name}`;
          return {
            ...lineItem,
            value: lineItemTotals[key] || 0,
          };
        });

        // Determine which line items have schedule items (should be disabled)
        const scheduleItemsSet = new Set(
          allScheduleItems.map((schedule) => `${schedule.category}::${schedule.line_item_name}`)
        );
        const lineItemsWithDisabledFlag = updatedLineItems.map((item) => ({
          ...item,
          isDisabled: scheduleItemsSet.has(`${item.category}::${item.name}`),
        }));

        // Clear existing schedule data before importing
        if (budgetSchedule.length > 0) {
          await removeBudgetScheduleData();
        }

        // Replace line items in the UI
        lineItemActions.replace(lineItemsWithDisabledFlag);

        // Update schedule items in the database
        if (allScheduleItems.length > 0) {
          await updateBudgetCountySchedule(allScheduleItems, true);
        }

        setTimeout(() => {
          enqueueSnackbar("Document imported successfully!", {
            preventDuplicate: true,
            variant: "success",
          });
        }, 1000);
      }
    } catch (error) {
      console.error("Error importing document:", error);
      enqueueSnackbar("Error importing document: " + (error.message || error), {
        preventDuplicate: true,
        variant: "error",
      });
    } finally {
      formStatusActions.setIsFormDirty(false, false);
    }
  };

  const handleDocumentSelectClose = async (documentContents) => {
    setIsDocumentSelectDialogOpen(false);

    if (documentContents) {
      await DocumentImport_Update(documentContents);
    }
  };

  const formMaxWidthShort = "800px";
  const formMaxWidth = "1000px";

  const updateBudgetCountySchedule = async (data, shouldReload = true) => {
    try {
      if (!data || data.length === 0) {
        if (shouldReload) {
          loadBudgetScheduleData(selectedField);
        }
        return;
      }

      const api = new ApiHelper({
        baseUrl: import.meta.env.VITE_DATANAC_USERS_API_URL_BASE,
        config: {},
        sessionManager: _sessionManager,
        // No caching:
        _instance: null,
      });

      await api.callPost(
        "BudgetCountyPracticeScheduleItem",
        {
          BudgetCountyPracticeScheduleItem: data,
          producer_token: globalState.producer_token,
          budget_county_practice_id: data[0].budget_county_practice_id,
        },
        globalState
      );
      if (shouldReload) {
        loadBudgetScheduleData(selectedField);
      }
    } catch (error) {
      handleError(error);
    }
  };

  const [budgetSchedule, setBudgetSchedule] = useState([]);

  // Use weather report hook for yield forecast data
  const {
    isLoadingYieldForecast,
    isLoadingCountyData,
    isLoadingHistoricalYieldTrend,
    yieldForecastValues,
    rmaProjectedYieldIrrigated,
    rmaProjectedYieldNonIrrigated,
    rmaHistoricalYieldIrrigated,
    rmaHistoricalYieldNonIrrigated,
  } = useWeatherReport({
    dateRange: { start: new Date(state.year, 0, 1), end: new Date(state.year, 11, 31) },
    locationState: {
      state_name: state.state_name,
      county_name: state.county_name,
      // sub_county_code: null, // Not needed for yield forecast
    },
    currentYear: state.year,
    selectedCropType: state.commodity_name,
    globalState,
    enqueueSnackbar,
  });

  const directIndirectSubTotal = (category) => {
    return round_ex(
      _.sumBy(
        _.filter(lineItems, (item) => item.category === category),
        (item) => Number(item.value) || 0
      ),
      4
    );
  };

  useEffect(() => {
    addAttachments([
      { name: "BudgetCountyPracticeLineItems", content: lineItems },
      { name: "BudgetCountyPracticeScheduleLineItems", content: budgetSchedule },
    ]);
  }, [lineItems, budgetSchedule]);

  const handleOnChangeRecords = async (records) => {
    try {
      // Collect all async operations without blocking
      const updatePromises = [];

      // Iterate through the records array and collect promises
      for (const record of records) {
        // Handle BudgetCountyPracticeLineItems updates
        if (record.BudgetCountyPracticeLineItems && record.BudgetCountyPracticeLineItems.length > 0) {
          // Merge the updated line items with the existing ones
          const updatedLineItems = [...lineItems];

          // Replace items in the array with updated versions (matching by id)
          record.BudgetCountyPracticeLineItems.forEach((updatedItem) => {
            // If id is null, assign new UUID and producer_token
            if (updatedItem.id === null || updatedItem.id === undefined) {
              updatedItem.id = uuidv4();
              updatedItem.producer_token = globalState.producer_token;
            }

            const existingIndex = updatedLineItems.findIndex((item) => item.id === updatedItem.id);
            if (existingIndex !== -1) {
              updatedLineItems[existingIndex] = updatedItem;
            } else {
              // If it's a new item, add it to the array
              updatedLineItems.push(updatedItem);
            }
          });

          const lineItemPromise = UsersApiHelper.updateBudgetCountyPracticeLineItem(
            { BudgetCountyPracticeLineItems: updatedLineItems },
            globalState
          ).catch((err) => {
            console.warn(err);
            enqueueSnackbar(err.detail || err.message || JSON.stringify(err), {
              preventDuplicate: true,
              variant: "error",
            });
            return { error: err }; // Return error info but don't throw
          });
          updatePromises.push(lineItemPromise);
        }

        // Handle BudgetCountyPracticeScheduleLineItems updates
        if (record.BudgetCountyPracticeScheduleLineItems && record.BudgetCountyPracticeScheduleLineItems.length > 0) {
          const currentInputSharePercent = budgetSchedule.length > 0 ? budgetSchedule[0]?.input_share_percent : null;

          // Process schedule items to assign IDs and producer tokens where needed
          record.BudgetCountyPracticeScheduleLineItems.forEach((scheduleItem) => {
            // If id is null, assign new UUID and producer_token
            if (scheduleItem.id === null || scheduleItem.id === undefined) {
              scheduleItem.id = uuidv4();
              scheduleItem.producer_token = globalState.producer_token;
              scheduleItem.input_share_percent = currentInputSharePercent;
            }
          });

          const schedulePromise = updateBudgetCountySchedule(record.BudgetCountyPracticeScheduleLineItems, false).catch(
            (err) => {
              console.warn(err);
              return { error: err }; // Return error info but don't throw
            }
          );
          updatePromises.push(schedulePromise);

          // Recalculate line item totals based on schedule items
          const updatedScheduleItems = [...budgetSchedule];

          // Update the schedule items array with new/updated items
          record.BudgetCountyPracticeScheduleLineItems.forEach((scheduleItem) => {
            const existingIndex = updatedScheduleItems.findIndex((item) => item.id === scheduleItem.id);
            if (existingIndex !== -1) {
              updatedScheduleItems[existingIndex] = scheduleItem;
            } else {
              updatedScheduleItems.push(scheduleItem);
            }
          });

          // Group by category::line_item_name and calculate totals
          const lineItemTotals = {};
          updatedScheduleItems
            .filter((item) => item.is_active !== false)
            .forEach((scheduleItem) => {
              if (scheduleItem.line_item_name && scheduleItem.category) {
                const key = `${scheduleItem.category}::${scheduleItem.line_item_name}`;
                const total = Number(scheduleItem.quantity || 0) * Number(scheduleItem.unit_value || 0);
                lineItemTotals[key] = round_ex((lineItemTotals[key] || 0) + total, 2);
              }
            });

          // Update corresponding line items with calculated totals
          const updatedLineItems = [...lineItems];
          Object.keys(lineItemTotals).forEach((key) => {
            const [category, lineItemName] = key.split("::");
            const lineItemIndex = updatedLineItems.findIndex(
              (item) => item.name === lineItemName && item.category === category
            );
            if (lineItemIndex !== -1) {
              updatedLineItems[lineItemIndex] = {
                ...updatedLineItems[lineItemIndex],
                value: lineItemTotals[key],
              };
            }
          });

          // Update line items if any totals changed
          if (Object.keys(lineItemTotals).length > 0) {
            const lineItemUpdatePromise = UsersApiHelper.updateBudgetCountyPracticeLineItem(
              { BudgetCountyPracticeLineItems: updatedLineItems },
              globalState
            ).catch((err) => {
              console.warn(err);
              enqueueSnackbar(err.detail || err.message || JSON.stringify(err), {
                preventDuplicate: true,
                variant: "error",
              });
              return { error: err };
            });
            updatePromises.push(lineItemUpdatePromise);
          }
        }
      }

      // Wait for all updates to complete without blocking the UI
      if (updatePromises.length > 0) {
        await Promise.allSettled(updatePromises);

        // Reload data to ensure UI is synchronized after all updates complete
        await loadData();
      }
    } catch (error) {
      console.error("Error updating records:", error);
      enqueueSnackbar("Failed to update records: " + (error.message || error), {
        preventDuplicate: true,
        variant: "error",
      });
    }
  };

  return (
    <>
      <WorkspaceHeader
        title={
          state?.commodity_name
            ? state?.commodity_name +
              " " +
              (state?.type_name && (state?.type_name !== "No Type Specified" ? " \u2013 " + state?.type_name : "")) +
              (state?.practice_name ? " \u2013 " + state?.practice_name : "")
            : "Budget"
        }
        category={{
          title: "Budgets",
          icon: <BudgetIcon />,
        }}
        breadCrumbs={[
          {
            title: "Farm Budgets",
            path: "/budget",
          },
          {
            title: state?.budget_name,
            path: `/budget/${budget_id}`,
          },
        ]}
      />

      <Box sx={{ width: "100%", maxWidth: formMaxWidthShort }}>
        <Grid container spacing={2}>
          <Grid item xs={12} md={12}>
            <Stack
              direction="row"
              spacing={2}
              alignItems="center"
              className="headerWithMenuButton headerWithMenuButtonAlignEnd"
            >
              <Box sx={{ display: "flex", alignItems: "center", minHeight: 65 }}>
                <Typography variant="h5">Crop</Typography>
              </Box>
              <Stack direction="row" spacing={1} alignItems="center" sx={{ minHeight: 65 }}>
                <ChatAssistantButton
                  systemPrompt={SYSTEM_PROMPT_BUDGET_COUNTY_PRACTICE}
                  attachments={[
                    { name: "Buget Line Items", content: lineItems },
                    { name: "Budget Schedules (Details)", content: budgetSchedule },
                  ]}
                  onChangeRecords={handleOnChangeRecords}
                />
                {formStatusComponent}
              </Stack>
            </Stack>
          </Grid>
          <Grid item xs={12} md={4}>
            <FormControl fullWidth={true}>
              <StatefulControlledTextField
                label="Name"
                name="name"
                isRequired={false}
                defaultValue={state.name || ""}
                variant="outlined"
                onChange={handleChangeState}
                InputLabelProps={{ shrink: true }}
                className="budget_name_input"
                isLoading={isLoading}
              />
            </FormControl>
          </Grid>
          <Grid item xs={12} md={4}>
            <DataFormSelect
              value={state.state_name}
              name="state_name"
              onChange={handleChangeState}
              objectName="State"
              itemFilter={{ reinsurance_year: state.year }}
              itemGetKey={(item) => item.state_name}
              itemGetLabel={(item) => item.state_name}
              doFetch={state?.year}
              isLoading={isLoading}
            />
          </Grid>
          <Grid item xs={12} md={4}>
            <DataFormSelect
              value={state.county_name}
              name="county_name"
              onChange={handleChangeState}
              objectName="County"
              itemFilter={{
                reinsurance_year: state.year,
                state_name: state.state_name,
              }}
              itemGetKey={(item) => item.county_name}
              itemGetLabel={(item) => item.county_name}
              doFetch={state.year && state.state_name}
              isLoading={isLoading}
            />
          </Grid>

          <Grid item xs={12} md={4}>
            {commodityDataFormSelect}
          </Grid>
          <Grid item xs={12} md={4}>
            {typeDataFormSelect}
          </Grid>
          <Grid item xs={12} md={4}>
            {practiceDataFormSelect}
          </Grid>
        </Grid>
      </Box>

      <Divider sx={{ mt: 2, mb: 2 }} />

      <Box sx={{ width: "100%", maxWidth: formMaxWidthShort }}>
        <Grid container spacing={2}>
          <Grid item xs={7}>
            <Typography variant="h5">Production</Typography>
          </Grid>
          <Grid item xs={5}>
            <Box display="flex" justifyContent="flex-end">
              <Button variant="outlined" onClick={() => setIsCountyDataDialogOpen(true)}>
                {isSmall ? "County" : "County History"}
                <InfoOutlined sx={{ ml: 1 }} />
              </Button>
            </Box>
          </Grid>

          <Grid item xs={12} md={4}>
            <FormControl fullWidth={true}>
              <StatefulControlledTextField
                label="Acres"
                name="reported_acreage"
                isRequired={true}
                defaultValue={state.reported_acreage || ""}
                variant="outlined"
                onChange={handleChangeState}
                InputLabelProps={{ shrink: true }}
                maskType={MASK_NUMERIC}
              />
            </FormControl>
          </Grid>
          <Grid item xs={12} md={4}>
            <FormControl fullWidth={true}>
              <StatefulControlledTextField
                label="Projected Yield/Acre"
                name="yield"
                isRequired={true}
                defaultValue={state.yield || ""}
                variant="outlined"
                onChange={handleChangeState}
                InputLabelProps={{ shrink: true }}
                maskType={MASK_NUMERIC}
              />
            </FormControl>
          </Grid>
          <Grid item xs={12} md={4}>
            <FormControl fullWidth={true}>
              <StatefulControlledTextField
                label="Projected Price"
                name="projected_price"
                isRequired={true}
                defaultValue={state.projected_price || ""}
                variant="outlined"
                onChange={handleChangeState}
                InputLabelProps={{ shrink: true }}
                maskType={MASK_NUMERIC}
                endAdornment={
                  state?.projected_price_is_override ? (
                    <InputAdornment position="end">
                      <IconButton
                        size="small"
                        edge="end"
                        title="Reset to Board Price + Basis"
                        onMouseDown={(e) => e.preventDefault()}
                        onClick={() => { formStatusActions.setIsFormDirty(true, false); dispatch({ type: "update", payload: { key: "projected_price_is_override", value: false } }); }}
                      >
                        <RestartAlt fontSize="small" />
                      </IconButton>
                    </InputAdornment>
                  ) : null
                }
              />
            </FormControl>
          </Grid>
          <Grid item xs={false} md={4} />
          <Grid item xs={false} md={4} />
          <Grid item xs={12} md={4}>
            <FormControl fullWidth={true}>
              <StatefulControlledTextField
                label="Production Share %"
                name="production_share_percent"
                defaultValue={state.production_share_percent || ""}
                variant="outlined"
                onChange={handleChangeState}
                InputLabelProps={{ shrink: true }}
                maskType={MASK_PERCENTAGE}
                allowNull={true}
              />
            </FormControl>
          </Grid>
          {/* Board price & basis — read-only */}
          <Grid item xs={12}>
            <Card
              sx={{
                boxShadow: "none",
                borderRadius: "14px",
                border: "1px solid",
                borderColor: "grey.200",
                backgroundColor: "grey.100",
              }}
            >
              <CardContent sx={{ py: 2 }}>
                <Stack
                  direction={{ xs: "column", sm: "row" }}
                  spacing={{ xs: 2, sm: 6 }}
                  sx={{
                    justifyContent: "center",
                    alignItems: { xs: "stretch", sm: "flex-start" },
                  }}
                >
                  <Stack alignItems="center" spacing={0.5} sx={{ flex: "1 1 0", minWidth: 0 }}>
                    <Typography
                      variant="h5"
                      sx={{
                        textAlign: "center",
                        color: "text.secondary",
                        wordBreak: "break-word",
                      }}
                    >
                      {boardPrice !== null
                        ? `${formatPrice(boardPrice, 4)}${
                            boardPriceMarketSymbol ? ` (${boardPriceMarketSymbol})` : ""
                          }`
                        : "--"}
                    </Typography>
                    <Typography variant="body2" color="textSecondary">
                      Board Price
                    </Typography>
                  </Stack>
                  <Stack alignItems="center" spacing={0.5} sx={{ flex: "1 1 0", minWidth: 0 }}>
                    <Typography
                      variant="h5"
                      sx={{
                        textAlign: "center",
                        color: "text.secondary",
                        wordBreak: "break-word",
                      }}
                    >
                      {cropBasis !== null ? formatBasis(cropBasis) ?? "--" : "--"}
                    </Typography>
                    <Stack direction="row" spacing={0.5} alignItems="center">
                      <Typography variant="body2" color="textSecondary">
                        Basis
                      </Typography>
                      <IconButton
                        size="small"
                        title="Edit Basis (Crop Profile)"
                        disabled={!state?.year || !state?.commodity_name}
                        onClick={() => setIsBasisDialogOpen(true)}
                      >
                        <EditOutlined sx={{ fontSize: 14 }} />
                      </IconButton>
                    </Stack>
                  </Stack>
                </Stack>
              </CardContent>
            </Card>
          </Grid>
          {/* DATANAC Yield Forecast Card */}
          <Grid item xs={12}>
            <Card
              sx={{
                boxShadow: "none",
                borderRadius: "14px",
                border: "1px solid",
                borderColor: "grey.200",
                backgroundColor: "grey.100",
              }}
            >
              <CardContent>
                {isLoadingYieldForecast || isLoadingCountyData || isLoadingHistoricalYieldTrend ? (
                  <Box sx={{ display: "flex", justifyContent: "center", py: 2 }}>
                    <Typography variant="body2" color="text.secondary">
                      Loading...
                    </Typography>
                  </Box>
                ) : (
                  <Box>
                    <YieldForecast
                      selectedCropType={state.commodity_name}
                      isLoadingYieldForecast={isLoadingYieldForecast}
                      isLoadingCountyData={isLoadingCountyData}
                      isLoadingHistoricalYieldTrend={isLoadingHistoricalYieldTrend}
                      yieldForecastValues={yieldForecastValues}
                      rmaProjectedYieldIrrigated={rmaProjectedYieldIrrigated}
                      rmaProjectedYieldNonIrrigated={rmaProjectedYieldNonIrrigated}
                      rmaHistoricalYieldIrrigated={rmaHistoricalYieldIrrigated}
                      rmaHistoricalYieldNonIrrigated={rmaHistoricalYieldNonIrrigated}
                    />

                    {/* Third row: Price information */}
                    {(prices?.projected_price || futures?.projected_price) && (
                      <Grid
                        container
                        spacing={1}
                        sx={{ mt: 2, pt: 2, borderTop: "1px solid", borderColor: "grey.200" }}
                      >
                        <Grid item xs={6} sx={{ textAlign: "center" }}>
                          {prices?.projected_price && (
                            <Box>
                              <Typography variant="h5" sx={{ color: "text.primary" }}>
                                {formatPrice(prices?.projected_price, 4)}
                              </Typography>
                              <Typography variant="body2" color="textSecondary">
                                USDA Insurance Price
                              </Typography>
                            </Box>
                          )}
                        </Grid>
                        <Grid item xs={6} sx={{ textAlign: "center" }}>
                          {futures?.projected_price && (
                            <Box>
                              <Typography variant="h5" sx={{ color: "text.primary" }}>
                                {formatPrice(futures?.harvest_price || futures?.projected_price, 4)}
                              </Typography>
                              <Typography variant="body2" color="textSecondary">
                                Current Local Price
                              </Typography>
                            </Box>
                          )}
                        </Grid>
                      </Grid>
                    )}
                  </Box>
                )}
              </CardContent>
            </Card>
          </Grid>
        </Grid>
      </Box>

      <Divider sx={{ mt: 2, mb: 2 }} />

      <Box sx={{ width: "100%", maxWidth: formMaxWidth }}>
        <Grid container spacing={2}>
          <Grid item xs={7}>
            <Typography variant="h5">Direct Costs</Typography>
          </Grid>
          <Grid item xs={5}>
            <Box display="flex" justifyContent="flex-end">
              <PopupState variant="popover" popupId="import-budget-menu">
                {(popupState) => (
                  <>
                    <Button data-testid="import-budget-button" variant="outlined" {...bindTrigger(popupState)}>
                      <ArrowDropDownOutlined sx={{ mr: 1 }} />
                      Import Budget Numbers
                    </Button>
                    <Menu {...bindMenu(popupState)}>
                      <MenuItem
                        data-testid="import-national-budgets-button"
                        onClick={() => {
                          handleTemplateDialogOpen();
                          popupState.close();
                        }}
                      >
                        National Budgets
                      </MenuItem>
                      <MenuItem
                        data-testid="import-my-farm-vault-button"
                        onClick={() => {
                          setIsDocumentSelectDialogOpen(true);
                          popupState.close();
                        }}
                      >
                        <FarmVaultIcon sx={{ mr: 1 }} />
                        My Farm Vault
                      </MenuItem>
                    </Menu>
                  </>
                )}
              </PopupState>
            </Box>
          </Grid>

          {lineItems
            // .filter((item, index, self) =>  index === self.findIndex((t) => t.id === item.id))
            .filter((li) => li.category === BUDGET_CATEGORY_DIRECT)
            .filter((ii) => ii.is_active)
            .sort((a, b) => a.name?.localeCompare(b.name))
            .map((currentLineItem, index) => (
              <Grid item key={index} xs={6} md={4} xl={3}>
                <FormControl className="BudgetCountyPracticeItem" fullWidth={true}>
                  <StatefulControlledTextField
                    label={currentLineItem?.name}
                    name={currentLineItem?.name}
                    isRequired={true}
                    defaultValue={round_ex(currentLineItem?.value, 4)}
                    variant="outlined"
                    InputLabelProps={{ shrink: true }}
                    onChange={(e) => handleChangeState_LineItem(e, currentLineItem)}
                    endAdornment={
                      <InputAdornment position="end" className="adornments">
                        <IconButton
                          data-testid={`Open-schedule-${currentLineItem?.name}`}
                          onClick={(e) => handleFieldClick(e, currentLineItem)}
                          color="primary"
                        >
                          <FormatListBulleted />
                        </IconButton>
                        <IconButton
                          data-testid={`Delete ${currentLineItem?.name}`}
                          onClick={() => handleDeleteLineItem(currentLineItem)}
                          edge="end"
                          color="secondary"
                          disabled={isDeletingLineItem}
                        >
                          <DeleteIcon />
                        </IconButton>
                      </InputAdornment>
                    }
                    maskType={MASK_NUMERIC}
                    disabled={currentLineItem.isDisabled}
                  />
                </FormControl>
              </Grid>
            ))}

          <BudgetSchedulePopover
            isPopoverOpen={isPopoverOpen}
            anchorEl={anchorEl}
            handlePopoverClose={handlePopoverClose}
            budgetSchedule={budgetSchedule}
            onChange={updateBudgetCountySchedule}
            selectedField={selectedField}
            budgetCountyPracticeId={state?.id}
          />

          <Grid item xs={6} md={4} xl={3} className="navigation" display={"flex"} alignItems={"end"}>
            <Button
              data-testid="add-new-line-item-button"
              fullWidth
              sx={{ borderRadius: 1, mb: "21px" }}
              variant="outlined"
              color="secondary"
              onClick={() => handleClickAddNewLineItem(BUDGET_CATEGORY_DIRECT)}
              disabled={isLoading}
            >
              <AddCircleOutline />
            </Button>
          </Grid>
        </Grid>
        <Grid container spacing={1} className="class_direct_indirect_line_item">
          <Grid item xs={6} md={6} xl={9} sx={{ textAlign: "left", paddingTop: "0px !important" }}>
            <Typography sx={{ m: 0, p: 0 }}>Total Direct Costs</Typography>
          </Grid>
          <Grid item xs={6} md={6} xl={3} sx={{ textAlign: "right", paddingTop: "0px !important" }}>
            <Typography sx={{ m: 0, p: 0 }}>{directIndirectSubTotal(BUDGET_CATEGORY_DIRECT)}</Typography>
          </Grid>
        </Grid>
      </Box>

      <Divider sx={{ mt: 2, mb: 2 }} />

      <Box sx={{ width: "100%", maxWidth: formMaxWidth }}>
        <Grid container spacing={2}>
          <Grid item xs={12} md={12}>
            <Typography variant="h5">Indirect Costs</Typography>
          </Grid>
          {lineItems
            .filter((li) => li.category === BUDGET_CATEGORY_INDIRECT)
            .filter((ii) => ii.is_active)
            .sort((a, b) => a.name?.localeCompare(b.name))
            .map((currentLineItem, index) => (
              <Grid item key={index} xs={6} md={4} xl={3}>
                <FormControl className="BudgetCountyPracticeItem" fullWidth={true}>
                  <StatefulControlledTextField
                    label={currentLineItem?.name}
                    name={currentLineItem?.name}
                    isRequired={true}
                    defaultValue={currentLineItem?.value}
                    variant="outlined"
                    InputLabelProps={{ shrink: true }}
                    onChange={(e) => handleChangeState_LineItem(e, currentLineItem)}
                    endAdornment={
                      <InputAdornment position="end" className="adornments">
                        <IconButton onClick={(e) => handleFieldClick(e, currentLineItem)} color="primary">
                          <FormatListBulleted />
                        </IconButton>
                        <IconButton
                          data-testid={`Delete ${currentLineItem?.name}`}
                          onClick={() => handleDeleteLineItem(currentLineItem)}
                          edge="end"
                          color="secondary"
                          disabled={isDeletingLineItem}
                        >
                          <DeleteIcon />
                        </IconButton>
                      </InputAdornment>
                    }
                    maskType={MASK_NUMERIC}
                    disabled={currentLineItem.isDisabled}
                  />
                </FormControl>
              </Grid>
            ))}

          <Grid item xs={6} md={4} xl={3} className="navigation" display={"flex"} alignItems={"end"}>
            <Button
              fullWidth
              sx={{ borderRadius: 1, mb: "21px" }}
              variant="outlined"
              color="secondary"
              onClick={() => handleClickAddNewLineItem(BUDGET_CATEGORY_INDIRECT)}
              disabled={isLoading}
            >
              <AddCircleOutline />
            </Button>
          </Grid>
        </Grid>
        <Grid container spacing={1} className="class_direct_indirect_line_item">
          <Grid item xs={6} md={6} xl={9} sx={{ textAlign: "left", paddingTop: "0px !important" }}>
            <Typography sx={{ m: 0, p: 0 }}>Total Indirect Costs</Typography>
          </Grid>
          <Grid item xs={6} md={6} xl={3} sx={{ textAlign: "right", paddingTop: "0px !important" }}>
            <Typography sx={{ m: 0, p: 0 }}>{directIndirectSubTotal(BUDGET_CATEGORY_INDIRECT)}</Typography>
          </Grid>
        </Grid>
      </Box>

      <Box
        className="pageBreakBefore"
        sx={{
          ...(isXL && {
            top: "auto",
            bottom: 0,
            pb: 2,
            position: "sticky",
            width: "100%",
            backgroundColor: (theme) => theme.palette.background.paper,
            zIndex: 1000,
          }),
        }}
      >
        <Divider sx={{ mt: 2, mb: 2 }} />
        <Grid container justifyItems="flex-start" spacing={1} className="budget-county-practice-totals">
          <Grid item xs={12} md={6} xl={1}>
            <Typography variant="h1">Totals</Typography>
          </Grid>
          <Grid item xs={12} md={6} xl={1.5}>
            <Stack direction={isSmall ? "row-reverse" : "column"} justifyContent="space-between">
              <Typography variant="h5">{formatNumber(state?.reported_acreage, 2)}</Typography>
              <Typography variant="body1" sx={{ textTransform: isSmall ? "" : "uppercase" }}>
                Acres
              </Typography>
            </Stack>
          </Grid>
          <Grid item xs={12} md={6} xl={1.5} sx={{ backgroundColor: "grey.100" }}>
            <Stack direction={isSmall ? "row-reverse" : "column"} justifyContent="space-between">
              <Typography variant="h5">{formatPrice(state?.yield * state?.projected_price, 0)}</Typography>
              <Typography variant="body1" sx={{ textTransform: isSmall ? "" : "uppercase" }}>
                Rev/Acre
              </Typography>
            </Stack>
          </Grid>
          <Grid item xs={12} md={6} xl={1.7} sx={{ backgroundColor: "grey.100" }}>
            <Stack direction={isSmall ? "row-reverse" : "column"} justifyContent="space-between">
              <Typography variant="h5">
                {formatPrice(state?.yield * state?.projected_price * state?.reported_acreage, 0)}
              </Typography>
              <Typography variant="body1" sx={{ textTransform: isSmall ? "" : "uppercase" }}>
                Revenue
              </Typography>
            </Stack>
          </Grid>
          <Grid item xs={12} md={6} xl={1.5}>
            <Stack direction={isSmall ? "row-reverse" : "column"} justifyContent="space-between">
              <Typography variant="h5">{formatPrice(calculateProductionCost(), 0)}</Typography>
              <Typography variant="body1" sx={{ textTransform: isSmall ? "" : "uppercase" }}>
                Cost/Acre
              </Typography>
            </Stack>
          </Grid>
          <Grid item xs={12} md={6} xl={1.7}>
            <Stack direction={isSmall ? "row-reverse" : "column"} justifyContent="space-between">
              <Typography variant="h5">
                {formatPrice(calculateProductionCost() * state?.reported_acreage, 0)}
              </Typography>
              <Typography variant="body1" sx={{ textTransform: isSmall ? "" : "uppercase" }}>
                Cost
              </Typography>
            </Stack>
          </Grid>
          <Grid item xs={12} md={6} xl={1.5} sx={{ backgroundColor: "grey.100" }}>
            <Stack direction={isSmall ? "row-reverse" : "column"} justifyContent="space-between">
              <Typography variant="h5">
                {formatPrice(state?.yield * state?.projected_price - calculateProductionCost(), 0)}
              </Typography>
              <Typography variant="body1" sx={{ textTransform: isSmall ? "" : "uppercase" }}>
                Net Income/Acre
              </Typography>
            </Stack>
          </Grid>
          <Grid item xs={12} md={6} xl={1.5} sx={{ backgroundColor: "grey.100" }}>
            <Stack direction={isSmall ? "row-reverse" : "column"} justifyContent="space-between">
              <Typography variant="h5" sx={{ fontWeight: "bold !important" }}>
                {formatPrice(
                  (state?.yield * state?.projected_price - calculateProductionCost()) * state?.reported_acreage,
                  0
                )}
              </Typography>
              <Typography
                variant="body1"
                sx={{ fontWeight: "bold !important", textTransform: isSmall ? "" : "uppercase" }}
              >
                Net Income
              </Typography>
            </Stack>
          </Grid>
          <Grid item xs={12} md={6} xl={1.5}>
            <Stack direction={isSmall ? "row-reverse" : "column"} justifyContent="space-between">
              <Typography variant="h5">
                {formatPrice(calculateProductionCost() / Number(state?.yield || 1), 2)}
              </Typography>
              <Typography variant="body1" sx={{ textTransform: isSmall ? "" : "uppercase" }}>
                Breakeven $
              </Typography>
            </Stack>
          </Grid>
          <Grid item xs={12} md={6} xl={1.5}>
            <Stack direction={isSmall ? "row-reverse" : "column"} justifyContent="space-between">
              <Typography variant="h5">
                {formatNumber(calculateProductionCost() / Number(state?.projected_price || 1))}
              </Typography>
              <Typography variant="body1" sx={{ textTransform: isSmall ? "" : "uppercase" }}>
                Breakeven {unitOfMeasureAbbreviation}
              </Typography>
            </Stack>
          </Grid>
        </Grid>
      </Box>

      {state.state_name && state.county_name && state.commodity_name && state.type_name && state.practice_name && (
        <Dialog open={isCountyDataDialogOpen} fullWidth>
          <DialogContent>
            <CountyDataView
              countyPractice={{
                reinsurance_year: getCurrentReinsuranceYear(),
                location_state_name: state?.state_name,
                location_county_name: state?.county_name,
                ...state,
              }}
            />
          </DialogContent>
          <DialogActions>
            <Button
              sx={{ mr: 1 }}
              variant="outlined"
              onClick={() => {
                setIsCountyDataDialogOpen(false);
              }}
            >
              <CloseOutlined sx={{ mr: 1 }} />
              Close
            </Button>
          </DialogActions>
        </Dialog>
      )}

      <Dialog open={isBasisDialogOpen} fullWidth maxWidth="md" onClose={() => setIsBasisDialogOpen(false)}>
        <CropProfileEditDialog
          cropProfile={{
            // CropProfileEditDialog.validateForm() rejects marketing_goal_units=0.
            // Server's CropProfile auto-insert (BudgetCountyPracticeUpdate) uses
            // floor(SUM(reported_acreage * yield)); seed the same default so the
            // dialog can save even when no CropProfile row exists yet.
            marketing_goal_units:
              Math.floor((Number(state?.reported_acreage) || 0) * (Number(state?.yield) || 0)) || 1,
            ...(cropProfile || {}),
          }}
          initialValues={{ year: state?.year, commodity_name: state?.commodity_name }}
          readOnlyFields={["year", "commodity_name"]}
          onClose={() => setIsBasisDialogOpen(false)}
          onChange={handleBasisSave}
        />
      </Dialog>

      <BudgetTemplateDialog
        open={isTemplateDialogOpen}
        onClose={handleTemplateDialogClose}
        onSelectTemplate={handleTemplateSelect}
        state={state}
        globalState={globalState}
      />

      <DocumentSelectDialog
        open={isDocumentSelectDialogOpen}
        onClose={handleDocumentSelectClose}
        document_type_name={DOCUMENT_FORM_NAME_BUDGET_CROPS}
      />

      {prompt?.component}
      {confirmDialogComponent}
    </>
  );
}

